<template>
    <div
        :class="[
            'input-wrapper',
            'input-wrapper--field',
            'input-wrapper--' + type,
            'input-wrapper--' + name,
        ]"
        @click="$emit('click')"
    >
        <label
            :class="[
                error ? 'has-error' : '',
                labelShowsValid && isValid ? 'valid' : '',
                checkIsTaxOfficeField() ? 'valid' : '',
            ]"
        >
            <input
                :type="type"
                :maxlength="maxLength"
                :name="name"
                :disabled="disabled"
                :readonly="readonly"
                :value="model ?? ''"
                :placeholder="placeholder"
                :autocomplete="suggestions ? 'on' : 'nope'"
                :inputmode="isNumeric ? 'decimal' : 'text'"
                @input="handleInput"
                @blur="onBlur"
                @focus="focusEvent"
                @paste="pasteHandler"
                @keypress="filterInput"
            />
            <div
                :class="[
                    model?.length ? 'not-empty' : '',
                    'input__label',
                    'input__label--' + name,
                    'input__label--' + type,
                ]"
            >
                <slot />
            </div>
        </label>
        <Error v-if="handlesError" :name="name" :value-field="model" />
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import Error from '@/components/basic/Error.vue';

export default defineComponent({
    name: 'Field',
    components: { Error },
    props: {
        type: {
            type: String,
            default: '',
        },
        name: {
            type: String,
            default: '',
        },
        modelValue: {
            type: String,
            default: '',
        },
        maxLength: {
            type: Number,
            default: null,
        },
        placeholder: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        labelShowsValid: {
            type: Boolean,
            default: true,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        handlesError: {
            type: Boolean,
            default: true,
        },
        isValidated: {
            type: Boolean,
            default: true,
        },
        handlesDataPreload: {
            type: Boolean,
            default: true,
        },
        suggestions: {
            type: Boolean,
            default: false,
        },
        disablePaste: {
            type: Boolean,
            default: false,
        },
        onlyNumbers: {
            type: Boolean,
            default: false,
        },
        standardFilter: {
            type: Boolean,
            default: false,
        },
        streetFilter: {
            type: Boolean,
            default: false,
        },
        allowAllCharacters: {
            type: Boolean,
            default: false,
        },
        isNumeric: {
            type: Boolean,
            default: false,
        },
        forceUppercase: {
            type: Boolean,
            default: false,
        },
        removeWhitespace: {
            type: Boolean,
            default: false,
        },
        noAutoValidation: {
            type: Boolean,
            default: false,
        },
    },
    emits: [
        'click',
        'dataPreloadComplete',
        'input',
        'keyup',
        'focus',
        'blur',
        'update:modelValue',
    ],
    setup(props, { emit }) {
        const store = useStore();
        const isValid = ref(false);
        const normalizeValue = (value: string | null | undefined): string => {
            if (typeof value !== 'string') {
                return '';
            }

            let normalizedValue = value;

            if (props.removeWhitespace) {
                normalizedValue = normalizedValue.replace(/\s+/g, '');
            }

            if (props.forceUppercase) {
                normalizedValue = normalizedValue.toUpperCase();
            }

            return normalizedValue;
        };
        const model = ref<string>(normalizeValue(props.modelValue));
        const isDataLoading = ref(false);
        const validateCity = ref(true);
        const validateStreet = ref(true);
        const validateBuildingNumber = ref(true);
        const validateApartmentNumber = ref(true);
        const standardCharactersFilter = /^[\p{L}/,.'-:()’\d\s-]+$/u;
        const streetCharactersFilter = /^[\p{L}/,.':()"&*’\d\s-]+$/u;
        const numbersFilter = /^\d+$/;

        const formDataStore = computed(() => store.state.formData);
        const isValidFieldsChanged = computed(
            () => store.state.isValidFieldsChanged
        );
        const hasError = computed(
            () =>
                Boolean(store.state.errors.get(props.name)) &&
                !checkIsTaxOfficeField()
        );

        const filterInput = (evt: any) => {
            if (evt.key === 'Enter') {
                return;
            }

            if (props.removeWhitespace && /\s/.test(evt.key)) {
                evt.preventDefault();
                return;
            }

            if (!props.allowAllCharacters) {
                if (props.onlyNumbers) {
                    if (
                        !numbersFilter.test(evt.key) ||
                        evt.which === 42 ||
                        evt.which === 43
                    ) {
                        evt.preventDefault();
                    }
                    return;
                }

                if (props.streetFilter) {
                    if (
                        !streetCharactersFilter.test(evt.key) ||
                        evt.which === 42 ||
                        evt.which === 43
                    ) {
                        evt.preventDefault();
                    }
                    return;
                }

                if (!props.standardFilter) {
                    if (
                        !standardCharactersFilter.test(evt.key) ||
                        evt.which === 42 ||
                        evt.which === 43
                    ) {
                        evt.preventDefault();
                    }
                    return;
                }
            }
        };

        const loadData = () => {
            const formData = formDataStore.value as Record<string, any>;

            if (
                Object.prototype.hasOwnProperty.call(formData, props.name) &&
                formData[props.name] !== null
            ) {
                isDataLoading.value = true;

                model.value = normalizeValue(formData[props.name]);
            }
            emit('dataPreloadComplete', model.value);
        };

        const handleInput = (evt: Event) => {
            const target = evt.target as HTMLInputElement;
            const normalizedValue = normalizeValue(target.value) ?? '';

            if (target.value !== normalizedValue) {
                target.value = normalizedValue;
            }

            model.value = normalizedValue;
            emit('input', normalizedValue);
        };

        const focusEvent = () => {
            emit('keyup');
            emit('focus');
        };

        const checkIsTaxOfficeField = () => {
            const searchedText = 'PolandTaxOfficeName';

            return (
                props.name.indexOf(searchedText) > -1 &&
                Boolean(model.value?.length)
            );
        };

        const validate = () => {
            if (props.isValidated) {
                store.dispatch('validate', {
                    name: props.name,
                    value: model.value,
                });
            }
            emit('blur', model.value);
            validateCity.value = false;
            validateStreet.value = false;
            validateBuildingNumber.value = false;
            validateApartmentNumber.value = false;
        };

        const pasteHandler = (evt: any) => {
            if (!props.allowAllCharacters) {
                if (props.onlyNumbers) {
                    if (
                        !numbersFilter.test(evt.clipboardData.getData('text'))
                    ) {
                        evt.preventDefault();
                    }
                }

                if (!props.standardFilter) {
                    if (
                        !standardCharactersFilter.test(
                            evt.clipboardData.getData('text')
                        )
                    ) {
                        evt.preventDefault();
                    }
                }
            }
            if (props.disablePaste) {
                evt.preventDefault();
            }
        };

        const onBlur = () => {
            emit('blur', model.value);
            if (props.noAutoValidation) {
                return;
            }
            validate();
        };

        const getInputValue = (selector: string): string => {
            const element = document.querySelector(
                selector
            ) as HTMLInputElement | null;
            return element?.value ?? '';
        };

        watch(isValidFieldsChanged, () => {
            if (props.name === 'postalCode') {
                model.value = getInputValue('input[name="postalCode"]');
                emit('update:modelValue', model.value);
            }
            if (props.name === 'city') {
                model.value = getInputValue('input[name="city"]');
                emit('update:modelValue', model.value);
                if (model.value && validateCity.value) {
                    validate();
                }
            }
            if (props.name === 'street') {
                model.value = getInputValue('input[name="street"]');
                emit('update:modelValue', model.value);
                if (model.value && validateStreet.value) {
                    validate();
                }
            }
            if (props.name === 'buildingNumber') {
                model.value = getInputValue('input[name="buildingNumber"]');
                emit('update:modelValue', model.value);
                if (model.value && validateBuildingNumber.value) {
                    validate();
                }
            }
            if (props.name === 'apartmentNumber') {
                model.value = getInputValue('input[name="apartmentNumber"]');
                emit('update:modelValue', model.value);
                if (model.value && validateApartmentNumber.value) {
                    validate();
                }
            }
            isValid.value = store.state.validFields.has(props.name);
            if (isValid.value) {
                store.commit('DELETE_ERROR', props.name);
            }
        });

        watch(
            () => props.modelValue,
            (newValue) => {
                model.value = normalizeValue(newValue);
            }
        );
        watch(
            () => model.value,
            (newValue) => {
                emit('update:modelValue', newValue);
            }
        );

        watch(
            () => model.value,
            () => {
                if (!isDataLoading.value) {
                    store.commit('DELETE_ERROR', props.name);
                } else {
                    isDataLoading.value = false;
                }

                emit('update:modelValue', model.value);
            }
        );

        watch(hasError, () => {
            if (!hasError.value && checkIsTaxOfficeField()) {
                isValid.value = true;
            }
        });

        onMounted(() => {
            if (props.handlesDataPreload) {
                loadData();
            }

            if (model.value && model.value.length && props.name !== 'email') {
                validate();
            }
        });

        return {
            error: hasError,
            isValid,
            model,
            filterInput,
            loadData,
            focusEvent,
            checkIsTaxOfficeField,
            validate,
            pasteHandler,
            handleInput,
            onBlur,
        };
    },
});
</script>
